import { useEffect, useState, useMemo, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchAllCameraAndFovsForRbac,
  refreshCameraDevicesUnderCompanionDevices,
} from "../../../api/DeviceServiceManager";
import { getFovDetailedCalibrationType, getFovDetailedOnboardingType, getLicenseTypeDisplayed } from "../../../global/utility/Utils";
import {
  updateDevices,
  modalOpen,
  refreshCameraPage,
  resetTableFilterState,
  showNotificationBanner,
  resetOnboardingCamera,
} from "../../../store/actions";
import {
  getCameraDataSource,
  getCameraObjectbyId,
  getFilteredData,
  getFilterOptions,
  getFovObjWithStreamId,
  isNewBadge,
} from "../../../global/utility/deviceTableUtils";
import { Body, Title } from "@vds/typography";
import CustomHOCTable from "../../../global/components/Tables/CustomHOCTable";
import { Button, TextLink } from "@vds/buttons";
import {
  getFilterIcon,
  getSortingIcon,
} from "../../../global/utility/tableUtils";
import { Badge } from "@vds/badges";
import { Tooltip } from "antd";
import { Tooltip as MuiTooltip } from "@mui/material";
import {
  findImageForList,
  getFovDetailedStatus,
  handleApiError,
} from "../../../global/utility/Utils";
import { hasPermission } from "../../../global/utility/permissionUtils";
import { listRoi } from "../../../api/RoiServiceManager";
import { snapshotImage, StreamType } from "../../../hoc/snapshotImage";
import { TableSnapshot } from "../../../global/components/snapshots/TableSnapshot";
import DiscoverCamera from "../../../features/camerOnboarding/discoveredCameraOnboarding";
import ErrorTemplate from "../../../global/components/errorTemplate/ErrorTemplate";
import { TEXT } from "../../../global/constants/errorTemplateConstants";
import { Box } from "@mui/material";
import { Loader } from "@vds/loaders";
import GettingStartedModal from "../../Dashboard/GettingStartedModal";
import { DEVICE_CONSTANTS } from "../../../constants/DeviceConstants";
import { InfoOutlined } from "@mui/icons-material";
import { capitalizeWords } from "../../../api/utils";
import styles from "./newCameraTab.module.css";
import { PAGE_IDENTIFIERS,TOOLTIP_TITLES } from "../../../global/constants/tableConstants";
import { setDetailNavSource } from "../../../hooks/usePaginationReset";
import { pageTypes } from "../../../global/constants/pageTypes";
import { BANNER_TYPES } from "../../../global/constants/constants";
import { LICENSE_NOTIFICATION_MESSAGES, LICENSE_NOTIFICATION_TYPES, getActiveLicenseSku, LICENSE_SKU_PATTERNS } from "../../../global/constants/licensesConstants";

const SnapshotCell = memo(({ streamId, cameraId, cameraName, streamName }) => {
  const SnapshotImg = useMemo(
    () => snapshotImage(TableSnapshot, streamId, cameraId, StreamType.PAIRED_CAMERA),
    [streamId, cameraId]
  );
  return <SnapshotImg cameraName={cameraName} streamName={streamName} />;
}, (prevProps, nextProps) => 
  prevProps.streamId === nextProps.streamId && 
  prevProps.cameraId === nextProps.cameraId &&
  prevProps.cameraName === nextProps.cameraName &&
  prevProps.streamName === nextProps.streamName
);

const NewCameraTab = (props) => {
  const dispatch = useDispatch();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const { pageName, parentDeviceId, companionSiteId = null } = props;
  const cameraPageIdentifier = pageName === pageTypes.COMPANION_DEVICE_INFO
    ? PAGE_IDENTIFIERS.ASSOCIATED_CAMERAS_TAB
    : PAGE_IDENTIFIERS.CAMERAS_TAB;
  const hasCachedCameras = (props.cameraData.cameraList?.length ?? 0) > 0;
  const [loading, setLoading] = useState(!hasCachedCameras);
  const [cameraDataSource, setCameraDataSource] = useState(null);
  const [cameraList, setCameraList] = useState(props.cameraData.cameraList || []);
  const [isCameraError, setCameraError] = useState(false);
  const [isNoCameras, setNoCameras] = useState(false);
  const [showCameraOnboardingModal, setShowCameraOnboardingModal] = useState(false);

  const fovList = props.cameraData.fovList;
  const cameraDataa = props.cameraData.cameraList;
  const companionList = props.cameraData.companionDeviceList;
  const navigate = useNavigate();
  const refreshPage = useSelector(
    (state) => state.deviceReducer.refreshCameraStatus
  );
  const licenseList = useSelector((state) => state.licenseReducer.licenses);
  const modalOpenObj = useSelector((state) => state.cameraOnboardingReducer?.modalOpenObj) || {};
  const isDiscoverCameraOpen = Object.keys(modalOpenObj).length > 0;

  useEffect(() => {
    if (props.cameraData?.cameraList) {
      const dataChanged = JSON.stringify(cameraList) !== JSON.stringify(props.cameraData.cameraList);
      setCameraList(props.cameraData.cameraList);
      if (dataChanged) {
        setTableKey(prev => prev + 1);
      }
    }
  }, [props.cameraData?.cameraList]);

  const handleCameraLink = (state) => {
    setDetailNavSource(PAGE_IDENTIFIERS.CAMERAS_TAB);
    navigate(`/cameraInfoPage?cameraId=${state?.data?.id}`);
  };

  const showLicenseSuspendedBanner = () => {
    const notificationInfo = {
      type: BANNER_TYPES.INFORMATION,
      priority: BANNER_TYPES.WARNING,
      showNotification: true,
      title: LICENSE_NOTIFICATION_TYPES.LICENSE_SUSPENDED,
      subtitle: LICENSE_NOTIFICATION_MESSAGES.LICENSE_SUSPENDED_MESSAGE,
    };
    dispatch(showNotificationBanner(notificationInfo));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getAllLicenseTypesDisplayed = (licenses, licenseList) => {
    if (!licenses?.length) return "No License";
    return licenses?.map((lic) => getLicenseTypeDisplayed(lic, licenseList))?.join(", ");
  };

  const getLicenseInOrder = (licenses) => {
    if (licenses?.[0] === LICENSE_SKU_PATTERNS.ENROLLED_APPEARANCE_V2) {
      return [...licenses].reverse();
    }
    return licenses;
  };

  const handleEditAppsClick = (record) => {
    const isLicenseSuspended = record?.licenseStatus?.toLowerCase() === "suspended";
    if (isLicenseSuspended) {
      showLicenseSuspendedBanner();
      return;
    }
    sessionStorage.setItem("selectedCameraInfo", "");
    setDetailNavSource(PAGE_IDENTIFIERS.CAMERAS_TAB);
    navigate("/roi", {
      state: {
        data: getCameraObjectbyId(record?.CameraId, cameraList)[0],
        streamData: getFovObjWithStreamId(record?.StreamId, fovList),
        FOVId: record?.StreamId,
        FOVSnapshot: record?.Snapshot,
        fieldOfViews: record?.Streams,
        FOVName: record?.Streams,
        originPage: "cameraDetails",
      },
    });
  };

  const columns = useMemo(() => [
    {
      title: (
        <div style={{ display: "flex" }}>
          <Title size="small" bold primitive="h2" viewport="desktop">
            Snapshot
          </Title>
        </div>
      ),
      dataIndex: "Snapshot",
      key: "Snapshot",
      render: (text, record) => (
        <SnapshotCell
          streamId={record?.StreamId}
          cameraId={record?.CameraId}
          cameraName={record?.Camera}
          streamName={record?.Streams}
        />
      ),
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Camera
        </Title>
      ),
      dataIndex: "Camera",
      key: "Camera",
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => {
        const cameraA = a.Camera || "";
        const cameraB = b.Camera || "";
        return cameraA.localeCompare(cameraB);
      },
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      ellipsis: true,
      render: (text, record) => {
        const cameraObj = getCameraObjectbyId(record.CameraId, cameraList);
        const state = {
          data: { ...cameraObj[0], cameraId: record.CameraId },
          title: `More info for ${record.Camera}`,
          fields: cameraObj[0]?.fields || {},
        };
        const showBadge = isNewBadge(record.lastUpdatedDate);
        return (
          <div style={{ display: "flex" }}>
            <span style={{ marginRight: "5px" }}>
              {showBadge && <Badge fillColor="black">New</Badge>}
            </span>
            <Tooltip
              placement="bottomLeft"
              autoAdjustOverflow="false"
              title={text}
            >
              <TextLink
                type="standAlone"
                data-testid="devices_cameraTabCameraLink"
                onClick={() => handleCameraLink(state)}
                size="large"
                className="link"
                style={{
                  marginLeft: "10px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  display: "block",
                  borderBottomStyle: "none",
                  overflow: "hidden",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                <Body size="large" viewport="desktop" primitive="span">
                  {text}
                </Body>
              </TextLink>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Stream
        </Title>
      ),
      dataIndex: "Streams",
      key: "Streams",
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => {
        return a.Streams.length - b.Streams.length;
      },
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      customStyle: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
        borderBottomStyle: "none",
        overflow: "hidden",
        cursor: "pointer",
      },
      ellipsis: true,
      render: (text, record) => {
        return (
          <div className="streamParentDivContainer">
            <div
            className="streamParentDivContainerChildDiv"
            >
              <Tooltip
                placement="bottomLeft"
                autoAdjustOverflow="false"
                title={text}
              >
                {record.Streams}
              </Tooltip>
            </div>
            {record.StreamId && (
              <MuiTooltip
                placement="top"
                title={
                  <div className={styles.tooltipContent}>
                    <Body viewport="desktop" size="small" bold={true}>
                      {TOOLTIP_TITLES.STREAM_ID}
                    </Body>
                    <Body size="small" viewport="desktop">
                      {record.StreamId}
                    </Body>
                    <Body viewport="desktop" size="small" bold={true}>
                      {TOOLTIP_TITLES.ONBOARDING_TYPE}
                    </Body>
                    <Body size="small" viewport="desktop">
                      {getFovDetailedOnboardingType(record)}
                    </Body>
                    <Body viewport="desktop" size="small" bold={true}>
                      {TOOLTIP_TITLES.LICENSE_TYPE}
                    </Body>
                    <Body size="small" viewport="desktop">
                      {getAllLicenseTypesDisplayed(getLicenseInOrder(record?.licensesinfo?.map((lic) => lic?.sku).filter(Boolean)), licenseList)}
                    </Body>
                  </div>
                }
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "white",
                      border: "1px solid black",
                      "& .MuiTooltip-arrow": {
                        backgroundColor: "white",
                      },
                    },
                  },
                }}
              >
                <span className="streamParentDivContainerChildDivMultiTooltipSpan">
                  <InfoOutlined fontSize="small" />
                </span>
              </MuiTooltip>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Status
        </Title>
      ),
      dataIndex: "Status",
      key: "Status",
      isFilter: true,
      filter: getFilterOptions(cameraDataSource, "Status", "cameraTab"),
      render: (text, record) => {
        const status = getFovDetailedStatus(record).status;
        if (record?.connectivityStatus?.toLowerCase() === DEVICE_CONSTANTS.CAMERA_OFFLINE_STATUS) {
          const showManualCalibrationInfo = record?.isManualAdded === true;
          return (
            <div className={styles.statusContainer}>
              <Badge fillColor={"yellow"}>{capitalizeWords(DEVICE_CONSTANTS.CAMERA_OFFLINE_STATUS)}</Badge>
              {showManualCalibrationInfo && (
                <MuiTooltip
                  placement="top"
                  title={
                    <div className={styles.tooltipContent}>
                      <Body viewport="desktop" size="small" bold={true}>
                        {" "}
                        RTSP camera offline{" "}
                      </Body>
                      <Body size="small" viewport="desktop">
                        {DEVICE_CONSTANTS.RTSP_CAMERA_OFFLINE_MESSAGE}
                      </Body>
                    </div>
                  }
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "white",
                        border: "1px solid black",
                        "& .MuiTooltip-arrow": {
                          backgroundColor: "white"
                        }
                      }
                    }
                  }}
                >
                  <sup className={styles.infoIcon}>
                    {" "}
                    <InfoOutlined fontSize="small" />{" "}
                  </sup>
                </MuiTooltip>
              )}
            </div>
          );
        }
        return status !== DEVICE_CONSTANTS.CAMERA_ONBOARDED_STATUS ? (
          <Badge fillColor={getFovDetailedStatus(record).color}>{status}</Badge>
        ) : (
          DEVICE_CONSTANTS.CAMERA_ONLINE_STATUS
        ); //getFovDetailedStatus(record).status;
      },
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Location
        </Title>
      ),
      dataIndex: "Location",
      key: "Location",
      isFilter: true,
      filter: getFilterOptions(cameraDataSource, "Location", "cameraTab"),
      onFilter: (value, record) => {
        return getFilteredData(value, record, "Location");
      },
      filterIcon: (filtered) => getFilterIcon(filtered),
      render: (record, text) => {
        if (record) {
          const getArea = () => {
            return text?.areaSelected ? text?.areaSelected?.name : null;
          };

          const getFloor = () => {
            return text?.floorSelected ? text?.floorSelected?.name : null;
          };

          if (pageName === pageTypes.COMPANION_DEVICE_INFO) {
            const area = getArea();
            const floor = getFloor();
            if (area && floor) {
              return (
                <div>
                  {area},{floor}
                </div>
              );
            } else if (area) {
              return <div>{area}</div>;
            } else if (floor) {
              return <div>{floor}</div>;
            } else {
              return <div>{record?.site?.name}</div>;
            }
          } else {
            return <div>{record?.site?.name}</div>;
          }
        } else {
          return null;
        }
      },
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Apps
        </Title>
      ),
      dataIndex: "Apps",
      key: "Apps",
      isDelete: true,
      render: (text, record) => {
        return <AddAppsComponent record={record} />;
      },
    },
    {
      dataIndex: "Companion",
      key: "Companion",
      filter: getFilterOptions(cameraDataSource, "Companion"),
      isFilter: true,
      onFilter: (value, record) => {
        return getFilteredData(value, record, "Companion");
      },
      width: 1,
      onHeaderCell: () => ({
        style: {
          padding: 0,
          border: "none",
        },
      }),
      render: () => null,
    },
  ], [cameraList, fovList, pageName, cameraDataSource]);

  const filteredColumns = useMemo(
    () => (pageName === pageTypes.COMPANION_DEVICE_INFO ? columns.filter((column) => column.key !== "Companion") : columns),
    [columns, pageName]
  );

  const AddAppsComponent = ({ record }) => {
    const streamImages = useSelector(
      (state) => state.imagesReducer.streamImages
    );
    const roiList = useSelector((state) => state.deviceReducer.roiList) || [];
    const status = getFovDetailedStatus(record).status;
    const isStreamCalibrated = status === "Onboarded";
    const hasApps = roiList.filter(roi => roi.parentdeviceid === record?.StreamId);
    const hasCvApps = hasApps.filter(item => item?.cvapps?.length > 0).length > 0;
    
    // Check if license is suspended
    const isLicenseSuspended = record?.licenseStatus?.toLowerCase() === "suspended";

    const showLicenseSuspendedBannerLocal = () => {
    const notificationInfo = {
      type: BANNER_TYPES.INFORMATION,
      priority: BANNER_TYPES.WARNING,
      showNotification: true,
      title: LICENSE_NOTIFICATION_TYPES.LICENSE_SUSPENDED,
      subtitle: LICENSE_NOTIFICATION_MESSAGES.LICENSE_SUSPENDED_MESSAGE,
    };
      dispatch(showNotificationBanner(notificationInfo));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getRoiApiResponse = async (id) => {
      setLoading(true);
      const payload = {
        accountname: activeSession?.accountId,
        //gwid: record?.CameraId,
        filter: {
          $expand: "fields,cvapp",
        },
        fovidentifiers: [
          {
            deviceid: id,
          },
        ],
      };
      const response = await listRoi(payload, activeSession);
      setLoading(false);
      return response;
    };

    const handleAddAppsClick = async (record) => {
      sessionStorage.setItem("selectedCameraInfo", "");
      if (isLicenseSuspended) {
        showLicenseSuspendedBannerLocal();
        return;
      }
      if (!hasPermission("Apps", "Create")) {
        showPermissionsBanner();
      } else {
        const roiApiResponse = await getRoiApiResponse(record?.StreamId);

        // Read SKU from raw fovList (source of truth) rather than table record
        // which may hold stale data. If FOV not found in Redux, default to empty
        // rather than falling back to potentially stale record.license.
        const rawFov = fovList.find(f => f.id === record?.StreamId);
        const skus = rawFov
          ? (rawFov.licenses?.length > 0 ? rawFov.licenses : rawFov.licensesinfo?.map(lic => lic.sku).filter(Boolean) || [])
          : [];
        const sku = getActiveLicenseSku(skus);
        const state = {
          fieldOfViews: record?.Streams,
          FOVImagesName: record?.Snapshot,
          FOVImageURL: findImageForList(streamImages, record?.StreamId),
          FOVNames: record?.Streams,
          FOVId: record?.StreamId,
          cameraId: record?.CameraId,
          companionId: record?.CompanionId,
          siteSelected: record?.siteSelected,
          floorSelected: record?.floorSelected,
          areaSelected: record?.areaSelected,
          subAreaSelected: record?.subAreaSelected,
          cameraname: record?.Camera,
          cameradata: cameraDataa,
          data: getCameraObjectbyId(record?.CameraId, cameraList)[0],
          roiList: roiApiResponse,
          sku: sku,
          originPage: "cameraDetails",
        };
        sessionStorage.setItem("selectedCameraInfo", JSON.stringify(state));
        setDetailNavSource(PAGE_IDENTIFIERS.CAMERAS_TAB);
        if (record?.license && record?.license.length > 0) {
          navigate("/cvAppSub", { state: state });
        } else {
          navigate("/cvAppsLicenseType", { state: state });
        }
      }
    };
    return (
      <div className={styles.buttonContainer}>
        <Button
          size="small"
          disabled={!isStreamCalibrated}
          use="primary"
          data-testid="devices_cameraTabAddApps"
          onClick={() => handleAddAppsClick(record)}
        >
          Add Apps
        </Button>
        <Button
          size="small"
          disabled={!isStreamCalibrated || !hasCvApps}
          use="secondary"
          data-testid="devices_cameraTabEditApps"
          onClick={() => handleEditAppsClick(record)}
        >
          Edit Apps
        </Button>
      </div>
    );
  };

  const initialStartUpApiCalls = (forceRefresh = false) => {
    if (pageName === pageTypes.COMPANION_DEVICE_INFO) {
       setLoading(true);
      if (!forceRefresh) {
        const cachedCameras = cameraDataa?.filter(
          (device) => device.parentdeviceid === parentDeviceId
        );
        if (cachedCameras?.length > 0) {
          setCameraList(cachedCameras);
          setLoading(false);
          return;
        }
      }
      refreshCameraDevicesUnderCompanionDevices(
        parentDeviceId,
        activeSession
      ).then((refreshedCameraDevices) => {
        if (
          refreshedCameraDevices !== null &&
          refreshedCameraDevices !== undefined
        ) {
          if (refreshedCameraDevices.length === 0) {
            setNoCameras(true);
          } else {
            setCameraList(refreshedCameraDevices);
          }
          setLoading(false);
        } else {
          console.error("Response is null or undefined");
          setCameraError(true);
          setLoading(false);
        }
      });
    } else {
      setCameraError(false);
      fetchAllCameraAndFovsForRbac(activeSession).then(
        (refreshedCameraDevices) => {
          if (
            refreshedCameraDevices !== null &&
            refreshedCameraDevices !== undefined
          ) {
            dispatch(updateDevices({
              cameraList: refreshedCameraDevices?.cameras,
              fovList: refreshedCameraDevices?.fovs,
            }));
            setCameraList(refreshedCameraDevices?.cameras);
            if (refreshedCameraDevices?.cameras) {
              sessionStorage.setItem(
                "camerasList",
                JSON.stringify(refreshedCameraDevices.cameras)
              );
            }
            setLoading(false);
          } else {
            console.error("Response is null or undefined");
            if (cameraDataa?.length > 0) {
              handleApiError("warning", DEVICE_CONSTANTS.FAILED_TO_UPDATE_CAMERA_LIST);
              setLoading(false);
            } else {
              setCameraError(true);
              setLoading(false);
            }
          }
        }
      ).catch(() => {
        if (cameraDataa?.length > 0) {
          handleApiError("warning", DEVICE_CONSTANTS.FAILED_TO_UPDATE_CAMERA_LIST);
          setLoading(false);
        } else {
          setCameraError(true);
          setLoading(false);
        }
      });
    }
  };

  useEffect(() => {
    initialStartUpApiCalls();
  }, []);

  useEffect(() => {
    if (refreshPage) {
      handleRefresh();
    }
  }, [refreshPage]);

  useEffect(() => {
    if (isCameraError && cameraDataa?.length > 0 && pageName === pageTypes.COMPANION_DEVICE_INFO) {
      const filtered = cameraDataa.filter(
        (device) => device.parentdeviceid === parentDeviceId
      );
      if (filtered.length > 0) {
        setCameraError(false);
        setCameraList(filtered);
        setLoading(false);
      }
      // No fallback data in Redux for this companion — error template remains visible
    } else if (!loading && !isCameraError && cameraList?.length === 0) {
      setNoCameras(true);
    }
  }, [cameraDataa, loading, isCameraError]);

  const handleRefresh = () => {
    setNoCameras(false);
    setCameraError(false);
    initialStartUpApiCalls(true);
    setLoading(true);
    dispatch(refreshCameraPage(false));
  }

  const getCameraData = () => {
    let cameraData = getCameraDataSource(cameraList, fovList, companionList);
    const filteredCameraId = cameraList?.map((item) => item.id);
    const filteredData = cameraData?.filter((item) =>
      filteredCameraId?.includes(item?.CameraId)
    );
    setCameraDataSource(filteredData);
  };

  useEffect(() => {
    getCameraData();
  }, [cameraList, fovList, companionList]);

  const CameraDeviceTable = useMemo(() => CustomHOCTable(), []);
  const [tableKey, setTableKey] = useState(0);

  const clearFilters = () => {
    sessionStorage.removeItem("searchTerm");
    dispatch(resetTableFilterState({
      pageIdentifier: cameraPageIdentifier,
      preserveCurrentPage: false
    }));
    setTableKey(prev => prev + 1);
  };

  const isCompanion = pageName === pageTypes.COMPANION_DEVICE_INFO;

  const customNoDataText = {
    emptyText: (
      <div style={{ color: "black" }}>
        <center>
          <br></br>
          {!cameraDataSource?.length > 0 && (
            <>
              <div style={{ marginBottom: "8px" }}>
                <Title size="large" primitive="h3">
                  No cameras
                </Title>
              </div>
            </>
          )}
          {cameraDataSource?.length > 0 && (
            <>
              <div style={{ marginBottom: "8px" }}>
                <Title size="large" primitive="h3">
                  No cameras found
                </Title>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <Body size="large" viewport="desktop">
                  Try adjusting your search or filters or check if there are any
                  unpaired cameras for the location.
                </Body>
              </div>

              <div>
                <Button
                  use="secondary"
                  data-testid="devices_cameraTabRemoveFilters"
                  onClick={clearFilters}
                >
                  Remove filters
                </Button>
              </div>
            </>
          )}
        </center>
      </div>
    ),
  };

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };
  const handleManualAdd = () => {
    dispatch(resetOnboardingCamera());
    setShowCameraOnboardingModal(true);
    dispatch(
      modalOpen({
        isPairing: true,
        onBoradType: "Manual",
      })
    );
  };

  if(isCameraError || isNoCameras) {
    const errorTitle = isCameraError
      ? TEXT.CAMERA_ERROR
      : isCompanion ? TEXT.NO_STREAMS_FOUND : TEXT.NO_CAMERAS_FOUND;
    const errorDesc = isCameraError
      ? TEXT.CAMERA_ERROR_DESC
      : isCompanion ? TEXT.NO_STREAMS_FOUND_DESC : TEXT.NO_CAMERAS_FOUND_DESC;

    return (
      <>
        {showCameraOnboardingModal && (
          <DiscoverCamera
            onClose={() => {
              dispatch(modalOpen({}));
              setShowCameraOnboardingModal(false);
            }}
          />
        )}
        {!isCompanion && (
          <div className={styles.manualAddButtonContainerEmpty}>
            <Button
              use="secondary"
              size="small"
              State="Default"
              width="auto"
              data-testid="devices_cameraTabManualCameraAddButton"
              onClick={() => handleManualAdd()}
            >
              Manual camera add
            </Button>
          </div>
        )}
        <Box sx={{ height: "600px" }}>
          <ErrorTemplate
            title={errorTitle}
            description={errorDesc}
            testId={isCameraError ? "camera_error" : "camera_empty"}
            templateType={isCameraError ? undefined : "info"}
            onRefresh={handleRefresh}
          />
        </Box>
      </>
    );
  }

  if(loading) {
    return (
      <Box
        sx={{
          height: "600px",
          position: 'relative'
        }}
      >
        <Loader active={loading} fullscreen={false} />
      </Box>
    );
  }

  return (
    <>
      <GettingStartedModal />
      {showCameraOnboardingModal && (
        <DiscoverCamera
          onClose={() => {
            dispatch(modalOpen({}));
            setShowCameraOnboardingModal(false);
          }}
        />
      )}
      <div style={{ maxWidth: "100%" }}>
        <CameraDeviceTable
          key={tableKey}
          showSearch={pageName === pageTypes.COMPANION_DEVICE_INFO ? false : true}
          showTitle={
            pageName === pageTypes.COMPANION_DEVICE_INFO ? "  Associated Cameras" : ""
          }
          columns={filteredColumns}
          dataList={cameraDataSource}
          rowKey="key"
          locale={customNoDataText}
          addDetailsButton={false}
          loading={loading}
          searchTextPlaceholder={"Search cameras"}
          pageIdentifier={cameraPageIdentifier}
          useReduxPersistence={true}
          companionSiteId={pageName === pageTypes.COMPANION_DEVICE_INFO ? companionSiteId : null}
          closeFilterTrigger={isDiscoverCameraOpen}
          extraAction={
            pageName !== pageTypes.COMPANION_DEVICE_INFO ? (
              <div>
                <Button
                  use="secondary"
                  size="small"
                  State="Default"
                  width="auto"
                  data-testid="devices_cameraTabManualCameraAddButton"
                  onClick={() => handleManualAdd()}
                >
                  Manual camera add
                </Button>
              </div>
            ) : null
          }
        />
      </div>
    </>
  );
};

export default NewCameraTab;
